/**
 * The card behind a checklist item's NAME (owner, 2026-10-09: "so its confusing"). Every kind of item opens it: how to do it
 * in one or two lines, the rule it serves, the one input if the rule needs one (a reading, an answer or a photo), and a big
 * Done. Done is disabled, with its reason, until the input is given (core checkinput.ts cardNeeds). The circle of a one-tap item
 * ticks it with "Saved: <item>" and Undo for 30 s; the circle of anything else opens this card and never ticks.
 */
import { useState } from "react";
import { checkAffordance, inputKindForTask, type CheckInputKind } from "@logwave/core";
import { lazyInline } from "../lib/lazy.ts";
import { initialsOf } from "../lib/labels.ts";
import { savedOf } from "../lib/checkoff.ts";
import { useApi, useSession } from "../session.tsx";
import { useCheckoff } from "./Checkoff.tsx";
import { Pill, useToast } from "./ui.tsx";

export interface TaskRef { label: string; taskId: string; how?: string }
export const taskKind = (label: string): CheckInputKind => inputKindForTask({ title: label });
/** "Needs a reading ›": what the row says it will do. Null for a one-tap item. */
export const taskBadge = (label: string): string | null => checkAffordance(taskKind(label)).badge;
export function Badge({ text }: { text: string | null }) { return text ? <Pill tone="line">{text}</Pill> : null; }

/** Save a checklist item: the entry, then "Saved: <item>" with Undo for 30 s. Throws nothing: a failure is a toast. */
export function useTaskDone() {
  const api = useApi();
  const s = useSession();
  const toast = useToast();
  const checkoff = useCheckoff();
  const [busy, setBusy] = useState(false);
  async function complete(t: TaskRef, extra: { note?: string | null; photoBase64?: string } = {}, o: { onUndone?: () => void } = {}): Promise<boolean> {
    // The circle never ticks an item that needs an input (R1): without the card's input there is nothing to save.
    if (taskKind(t.label) !== "tap" && extra.note === undefined && !extra.photoBase64) return false;
    
    setBusy(true);
    try {
      const saved = await api.createEntry(s.org.id, { locationId: s.location.id, templateId: "task_done", taskId: t.taskId, values: { task: t.label, done: "yes", ...(extra.note ? { note: extra.note } : {}), initials: initialsOf(s.me.user.name) }, ...(extra.photoBase64 ? { photoBase64: extra.photoBase64 } : {}) });
      checkoff.done(t.label, savedOf("entry", s.org.id, { id: saved.id, at: saved.createdAt }), { text: `Saved: ${t.label}`, ...(o.onUndone ? { onUndone: o.onUndone } : {}) });
      return true;
    } catch (x) {
      toast.show(x instanceof Error ? x.message : "Couldn't save. Try again.", { icon: "alert" });
      return false;
    } finally { setBusy(false); }
  }
  return { complete, busy };
}

/**
 * The card itself (ItemCardDialog.tsx): a sheet that opens on a tap, so its file (with the photo row, core proof.ts) loads when it
 * is first drawn, off the main chunk. Same name and props for every caller.
 */
export const ItemCardDialog = lazyInline(() => import("./ItemCardDialog.tsx"), "ItemCardDialog");
